import { useState, useEffect } from "react";
import { Modal, Form, Input, Button, message, Dropdown, MenuProps, Popconfirm, Select } from "antd";
import { EditOutlined, DeleteOutlined, FolderOpenOutlined, SwapOutlined, SaveOutlined, CloseOutlined, FolderAddOutlined } from "@ant-design/icons";
import styled, { css } from "styled-components";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { newPostCategory, putCategory, deleteCategory, changeParentCategory, reorderCategory } from "../../../api/categoryAPI"; // reorderCategory 추가 필요
import { getLoginUser } from "../../../api/user";
import { getPermittedUsersForCategory, addCategoryPermission } from "../../../api/adminAPI";

interface SortableItemProps {
  category: any;
  isActive: boolean;
  isOrderEditing: boolean;
  isAdmin: boolean;
  contextMenuItems: MenuProps['items'];
  onClick: () => void;
  isVirtual?: boolean;
}

const SortableCategoryItem = ({ category, isActive, isOrderEditing, isAdmin, contextMenuItems, onClick, isVirtual }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id, disabled: !isOrderEditing || isVirtual });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
    touchAction: "none" as React.CSSProperties["touchAction"],
  };

  // 순서 변경 모드일 때
  if (isOrderEditing) {
    // 가상 항목은 드래그 비활성화 (그냥 표시만)
    if (isVirtual) {
      return (
        <CategoryItem $active={isActive} $isOrderMode={true} style={{ opacity: 0.6, cursor: 'not-allowed' }}>
          {category.name}
        </CategoryItem>
      );
    }
    return (
      <CategoryItem
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        $active={isActive}
        $isOrderMode={true}
      >
        {category.name}
      </CategoryItem>
    );
  }

  // 일반 모드 + 관리자 + 우클릭 메뉴 노출 가능 (가상 항목 제외)
  if (isAdmin && !isVirtual && contextMenuItems && contextMenuItems.length > 0) {
    return (
      <Dropdown menu={{ items: contextMenuItems }} trigger={['contextMenu']}>
        <CategoryItem
          $active={isActive}
          onClick={onClick}
          $isOrderMode={false}
        >
          {category.name}
        </CategoryItem>
      </Dropdown>
    );
  }

  // 일반 유저 또는 가상 항목
  return (
    <CategoryItem
      $active={isActive}
      onClick={onClick}
      $isOrderMode={false}
    >
      {category.name}
    </CategoryItem>
  );
};

interface Props {
  rootCategoryId: number;
  categoryType: string;
  categories: any[];
  activeCategory: string;
  parentCategoryList?: any[];
  onCategoryChange: (category: string) => void;
  reloadCategory?: () => Promise<void>;
  inheritPermissions?: boolean;
}

const MainCategoryMenu = ({ rootCategoryId, categoryType, categories, activeCategory, parentCategoryList, onCategoryChange, reloadCategory, inheritPermissions = false }: Props) => {
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Local state for DnD (props로 받은 categories를 로컬에서 조작하기 위함)
  const [localCategories, setLocalCategories] = useState<any[]>([]);

  // Toggle State
  const [isOrderEditing, setIsOrderEditing] = useState(false);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);

  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [movingCategory, setMovingCategory] = useState<any>(null);

  // Add 모달의 부모 카테고리 ID. null이면 root에 추가, 값이 있으면 해당 카테고리의 자식으로 추가.
  const [addingParentId, setAddingParentId] = useState<number | null>(null);
  const [addingParentName, setAddingParentName] = useState<string>("");

  const [confirmLoading, setConfirmLoading] = useState(false);
  
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [moveForm] = Form.useForm();

  // 두 번째 컬럼(서브 카테고리 메뉴)에서는 추가 깊이의 서브를 만들지 않음.
  // parentCategoryList의 유무로 판단: 없으면 최상위 메뉴.
  const isTopLevelMenu = !parentCategoryList || parentCategoryList.length === 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  useEffect(() => {
    const checkAdminPermission = async () => {
      try {
        const userData = await getLoginUser();
        setIsAdmin(userData?.role === 'ADMIN_MAIN');
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdminPermission();
  }, []);

  const handleCancel = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsMoveModalOpen(false);
    setEditingCategory(null);
    setMovingCategory(null);
    setAddingParentId(null);
    setAddingParentName("");
    form.resetFields();
    editForm.resetFields();
    moveForm.resetFields();
  };

  const onFinish = async (values: any) => {
    setConfirmLoading(true);
    try {
      // addingParentId가 지정되어 있으면 해당 카테고리의 자식(서브)으로 생성
      // null이면 기존처럼 root 자식으로 생성
      const parentId = addingParentId ?? rootCategoryId;
      const result = await newPostCategory(values.name, parentId, categoryType);

      // 서브 카테고리 생성 시 상위 카테고리의 권한을 가진 유저에게 동일한 권한 부여
      if (inheritPermissions && result?.data) {
        const permissionSourceId = addingParentId ?? (isTopLevelMenu ? null : rootCategoryId);
        if (permissionSourceId) {
          const newCategoryId = result.data;
          try {
            const permittedUserIds: number[] = await getPermittedUsersForCategory(permissionSourceId);
            if (permittedUserIds.length > 0) {
              await Promise.all(
                permittedUserIds.map(userId => addCategoryPermission(userId, newCategoryId))
              );
            }
          } catch {
            // 권한 부여 실패해도 카테고리 생성은 성공 처리
          }
        }
      }

      message.success(
        addingParentId
          ? "서브 카테고리가 추가되었습니다."
          : "카테고리가 추가되었습니다."
      );
      if (reloadCategory) await reloadCategory();
      handleCancel();
    } catch (error) {
      message.error("카테고리 생성에 실패했습니다.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const onEditFinish = async (values: any) => {
    setConfirmLoading(true);
    try {
      await putCategory(editingCategory.id, values.name);
      message.success("카테고리가 수정되었습니다.");
      if (reloadCategory) await reloadCategory();
      handleCancel();
    } catch (error) {
      message.error("카테고리 수정에 실패했습니다.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const onMoveFinish = async (values: any) => {
    setConfirmLoading(true);
    try {
      await changeParentCategory(movingCategory.id, values.parentId, 1);
      message.success("상위 카테고리가 변경되었습니다.");
      if (reloadCategory) await reloadCategory();
      handleCancel();
    } catch (error) {
      message.error("카테고리 이동에 실패했습니다.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDelete = async (categoryId: number) => {
    try {
      await deleteCategory(categoryId);
      message.success("카테고리가 삭제되었습니다.");
      if (reloadCategory) await reloadCategory();
    } catch (error) {
      message.error("카테고리 삭제에 실패했습니다.");
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setLocalCategories((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    try {
      // 가상 항목은 순서 저장 대상에서 제외
      const reorderData = localCategories
        .filter((cat) => !cat.__isVirtualMain)
        .map((cat, index) => ({ categoryId: cat.id, listOrder: index + 1 }));
      await reorderCategory(reorderData);
      message.success("순서가 저장되었습니다.");
      setIsOrderEditing(false);
      if (reloadCategory) await reloadCategory();
    } catch (e) {
      message.error("순서 저장 실패");
    }
  };

  const handleCancelOrder = () => {
    setIsOrderEditing(false);
    setLocalCategories(categories);
  };

  const getContextMenuItems = (category: any): MenuProps['items'] => {
    // 가상 항목은 컨텍스트 메뉴 없음
    if (category.__isVirtualMain) return [];

    const items: MenuProps['items'] = [
      {
        key: 'edit',
        label: '이름 수정',
        icon: <EditOutlined />,
        onClick: () => {
          setEditingCategory(category);
          editForm.setFieldsValue({ name: category.name });
          setIsEditModalOpen(true);
        }
      }
    ];

    // 최상위 메뉴(메인 카테고리)에서만 "서브 카테고리 추가" 노출
    // 서브 카테고리 메뉴(parentCategoryList가 있는 두 번째 컬럼)에서는 노출 안 함
    if (isTopLevelMenu) {
      items.push({
        key: 'addSub',
        label: '서브 카테고리 추가',
        icon: <FolderAddOutlined />,
        onClick: () => {
          setAddingParentId(category.id);
          setAddingParentName(category.name);
          form.resetFields();
          setIsAddModalOpen(true);
        }
      });
    }

    if (parentCategoryList && parentCategoryList.length > 0) {
      items.push({
        key: 'move',
        label: '상위 카테고리 변경',
        icon: <FolderOpenOutlined />,
        onClick: () => {
          setMovingCategory(category);
          moveForm.setFieldsValue({ parentId: rootCategoryId }); 
          setIsMoveModalOpen(true);
        }
      });
    }

    items.push({
      key: 'delete',
      label: (
        <Popconfirm
          title="카테고리 삭제"
          description="정말 삭제하시겠습니까?"
          onConfirm={() => handleDelete(category.id)}
          okText="삭제"
          cancelText="취소"
          okButtonProps={{ danger: true }}
        >
          <div style={{ color: '#ff4d4f' }}>삭제하기</div>
        </Popconfirm>
      ),
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      danger: true,
    });

    return items;
  };

  return (
    <MainCategoryMenuContainer>
      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter} 
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={localCategories.filter((c) => !c.__isVirtualMain).map(c => c.id)} 
          strategy={verticalListSortingStrategy}
        >
          <CategoryListWrapper $isOrderEditing={isOrderEditing}>
            {localCategories.map(category => (
              <SortableCategoryItem
                key={category.id}
                category={category}
                isActive={activeCategory === String(category.id)}
                isOrderEditing={isOrderEditing}
                isAdmin={isAdmin}
                contextMenuItems={getContextMenuItems(category)}
                onClick={() => onCategoryChange(String(category.id))}
                isVirtual={!!category.__isVirtualMain}
              />
            ))}
          </CategoryListWrapper>
        </SortableContext>
      </DndContext>
      
      {isAdmin && (
        <AdminControls>
          {!isOrderEditing ? (
            <>
              <AddCategoryItem onClick={() => {
                setAddingParentId(null);
                setAddingParentName("");
                form.resetFields();
                setIsAddModalOpen(true);
              }}>
                + Add
              </AddCategoryItem>
              {localCategories.filter(c => !c.__isVirtualMain).length > 1 && (
                 <IconButton onClick={() => setIsOrderEditing(true)} title="순서 변경">
                   <SwapOutlined />
                 </IconButton>
              )}
            </>
          ) : (
            <OrderControlBox>
               <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSaveOrder} style={{marginRight: 4}}>저장</Button>
               <Button size="small" icon={<CloseOutlined />} onClick={handleCancelOrder} danger>취소</Button>
            </OrderControlBox>
          )}
        </AdminControls>
      )}

      <Modal
        title={addingParentId ? `'${addingParentName}'에 서브 카테고리 추가` : "Add Category"}
        open={isAddModalOpen}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: '20px' }}>
          <Form.Item name="name" label="Category Name" rules={[{ required: true, message: '이름을 입력해주세요.' }]}>
            <Input placeholder={addingParentId ? "서브 카테고리 이름을 입력하세요" : "Enter new category name"} />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={confirmLoading}>Add</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Edit Category" open={isEditModalOpen} onCancel={handleCancel} footer={null} centered>
        <Form form={editForm} layout="vertical" onFinish={onEditFinish} style={{ marginTop: '20px' }}>
          <Form.Item name="name" label="Category Name" rules={[{ required: true, message: '이름을 입력해주세요.' }]}>
            <Input placeholder="Enter category name" />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={confirmLoading}>Save</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="상위 카테고리 변경" open={isMoveModalOpen} onCancel={handleCancel} footer={null} centered>
        <Form form={moveForm} layout="vertical" onFinish={onMoveFinish} style={{ marginTop: '20px' }}>
           <Form.Item 
            name="parentId" 
            label="새로운 상위 카테고리 선택" 
            rules={[{ required: true, message: '선택해주세요.' }]}
          >
            <Select 
              placeholder="상위 카테고리 선택"
              options={parentCategoryList?.map(p => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>취소</Button>
            <Button type="primary" htmlType="submit" loading={confirmLoading}>이동</Button>
          </Form.Item>
        </Form>
      </Modal>
    </MainCategoryMenuContainer>
  );
};

const MainCategoryMenuContainer = styled.div`
  width: fit-content;
  min-width: 200px;
  background-color: #f8f6fb;
  padding-bottom: 10px; 

  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 10px;
  }
`;

const CategoryListWrapper = styled.div<{ $isOrderEditing: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  
  ${props => props.$isOrderEditing && css`
    border: 2px dashed #1890ff;
    border-radius: 6px;
    background-color: #e6f7ff;
  `}

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CategoryItem = styled.div<{ $active: boolean, $isOrderMode?: boolean }>`
  padding: 12px 16px;
  border-radius: 4px;
  transition: background-color 0.2s, color 0.2s; 

  color: ${props => props.$active ? '#1890ff' : '#333'};
  background-color: ${props => props.$active ? '#e6f7ff' : 'transparent'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : 'normal'};

  ${props => props.$isOrderMode ? css`
    cursor: grab;
    border: 1px solid #d9d9d9;
    background-color: #fff;
    margin-bottom: 4px;
    &:active { cursor: grabbing; }
    &:hover { border-color: #40a9ff; }
  ` : css`
    cursor: pointer;
    &:hover { background-color: #f0f0f0; }
  `}

  @media (max-width: 768px) {
    white-space: nowrap;
    text-align: center;
  }
`;

const AdminControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  margin-top: 8px;
`;

const AddCategoryItem = styled.div`
  flex: 1;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px dashed #ccc;
  color: #888;
  font-size: 12px;
  text-align: center;
  margin-right: 5px;
  
  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const IconButton = styled.div`
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #eee;
  background: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const OrderControlBox = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 5px;
`;

export default MainCategoryMenu;